/**
 * Main Dashboard Component
 * Integrates 3D Azure Globe with real-time payment data
 */

import React, { useState, useEffect, useRef } from 'react';
import AzureGlobe from './AzureGlobe';
import './Dashboard.css';
import PromptPay from './PromptPay';
import './PromptPay.css';

const Dashboard = () => {
  const globeContainerRef = useRef(null);
  const globeRef = useRef(null);
  const [hemispheres, setHemispheres] = useState([]);
  const [selectedHemisphere, setSelectedHemisphere] = useState(null);
  const [paymentStats, setPaymentStats] = useState({});
  const [realTimeData, setRealTimeData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  // Initialize globe and fetch initial data
  useEffect(() => {
    initializeGlobe();
    fetchInitialData();
    setupWebSocket();
    
    return () => {
      if (globeRef.current) {
        globeRef.current.destroy();
      }
    };
  }, []);

  const initializeGlobe = () => {
    if (globeContainerRef.current) {
      globeRef.current = new AzureGlobe(globeContainerRef.current, {
        onHemisphereClick: handleHemisphereClick,
        autoRotate: true,
        rotateSpeed: 0.5
      });
    }
  };

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch hemispheres data
      const hemispheresResponse = await fetch('/api/hemispheres');
      const hemispheresData = await hemispheresResponse.json();
      
      if (hemispheresData.success) {
        setHemispheres(hemispheresData.data);
        
        // Create globe markers
        if (globeRef.current) {
          globeRef.current.createHemisphereMarkers(hemispheresData.data);
        }
        
        // Extract payment stats
        const stats = {};
        hemispheresData.data.forEach(hemisphere => {
          stats[hemisphere.id] = hemisphere.stats;
        });
        setPaymentStats(stats);
      }
      
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    try {
      // Always use the Worker API for WebSocket (Pages doesn't support upgrade)
      const wsUrl = process.env.REACT_APP_WS_URL || 'wss://asean-payment-verifier.sv9.workers.dev/ws';
      
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleRealTimeUpdate(data);
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(setupWebSocket, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
    } catch (error) {
      console.error('Error setting up WebSocket:', error);
    }
  };

  const handleRealTimeUpdate = (data) => {
    setRealTimeData(prev => [data, ...prev.slice(0, 99)]); // Keep last 100 events
    
    if (data.type === 'transaction_created' || data.type === 'transaction_verified') {
      // Update payment stats for the hemisphere
      const hemisphereId = data.data.hemisphere_id;
      
      setPaymentStats(prev => {
        const updatedStats = { ...prev };
        if (!updatedStats[hemisphereId]) {
          updatedStats[hemisphereId] = {
            total_transactions: 0,
            verified_transactions: 0,
            total_amount: 0,
            avg_amount: 0
          };
        }
        
        if (data.type === 'transaction_created') {
          updatedStats[hemisphereId].total_transactions++;
        } else if (data.type === 'transaction_verified') {
          updatedStats[hemisphereId].verified_transactions++;
          updatedStats[hemisphereId].total_amount += data.data.amount || 0;
          updatedStats[hemisphereId].avg_amount = 
            updatedStats[hemisphereId].total_amount / updatedStats[hemisphereId].verified_transactions;
        }
        
        // Update globe
        if (globeRef.current) {
          globeRef.current.updatePaymentData(hemisphereId, updatedStats[hemisphereId]);
        }
        
        return updatedStats;
      });
    }
  };

  const handleHemisphereClick = (hemisphere) => {
    setSelectedHemisphere(hemisphere);
    fetchHemisphereDetails(hemisphere.id);
  };

  const fetchHemisphereDetails = async (hemisphereId) => {
    try {
      const response = await fetch(`/api/hemisphere/${hemisphereId}/stats`);
      const data = await response.json();
      
      if (data.success) {
        // Update detailed stats
        setPaymentStats(prev => ({
          ...prev,
          [hemisphereId]: data.data.stats
        }));
      }
    } catch (error) {
      console.error('Error fetching hemisphere details:', error);
    }
  };

  const getTotalStats = () => {
    const total = hemispheres.reduce((acc, hemisphere) => {
      const stats = paymentStats[hemisphere.id] || {};
      return {
        transactions: acc.transactions + (stats.total_transactions || 0),
        verified: acc.verified + (stats.verified_transactions || 0),
        amount: acc.amount + (stats.total_amount || 0)
      };
    }, { transactions: 0, verified: 0, amount: 0 });
    
    return total;
  };

  const formatCurrency = (amount, currency = 'THB') => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading Payment Verification Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Payment Verification System v2.0</h1>
          <div className="connection-status">
            <span className={`status-indicator ${wsConnected ? 'connected' : 'disconnected'}`}>
              {wsConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Globe Section */}
        <section className="globe-section">
          <div className="globe-container" ref={globeContainerRef}></div>
          
           {/* Globe Controls */}
           <div className="globe-controls">
             <button 
               onClick={() => {
                 if (globeRef.current && globeRef.current.controls) {
                   globeRef.current.controls.autoRotate = !globeRef.current.controls.autoRotate;
                 }
               }}
               className="control-btn"
             >
               Toggle Rotation
             </button>
             <button 
               onClick={() => {
                 if (globeRef.current && globeRef.current.controls) {
                   globeRef.current.controls.reset();
                 }
               }}
               className="control-btn"
             >
               Reset View
             </button>
           </div>
        </section>


        {/* PromptPay Section */}
        <section className="promptpay-section">
          <PromptPay onPaymentSuccess={(data) => {
            // Handle successful payment
            console.log('Payment verified:', data);
          }} />
        </section>
        {/* Stats Section */}
        <section className="stats-section">
          {/* Total Stats */}
          <div className="stats-card total-stats">
            <h3>Total System Stats</h3>
            <div className="stat-grid">
              <div className="stat-item">
                <span className="stat-value">{getTotalStats().transactions}</span>
                <span className="stat-label">Total Transactions</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{getTotalStats().verified}</span>
                <span className="stat-label">Verified</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatCurrency(getTotalStats().amount)}</span>
                <span className="stat-label">Total Amount</span>
              </div>
            </div>
          </div>

          {/* Selected Hemisphere Details */}
          {selectedHemisphere && (
            <div className="stats-card hemisphere-details">
              <h3>{selectedHemisphere.name}</h3>
              <div className="hemisphere-info">
                <p><strong>Region:</strong> {selectedHemisphere.region_code}</p>
                <p><strong>Location:</strong> {selectedHemisphere.latitude.toFixed(4)}, {selectedHemisphere.longitude.toFixed(4)}</p>
                <p><strong>Radius:</strong> {selectedHemisphere.radius_km} km</p>
              </div>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-value">
                    {paymentStats[selectedHemisphere.id]?.total_transactions || 0}
                  </span>
                  <span className="stat-label">Transactions</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {paymentStats[selectedHemisphere.id]?.verified_transactions || 0}
                  </span>
                  <span className="stat-label">Verified</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {formatCurrency(paymentStats[selectedHemisphere.id]?.total_amount || 0)}
                  </span>
                  <span className="stat-label">Total Amount</span>
                </div>
              </div>
            </div>
          )}

          {/* Real-time Activity Feed */}
          <div className="stats-card activity-feed">
            <h3>Real-time Activity</h3>
            <div className="activity-list">
              {realTimeData.slice(0, 10).map((event, index) => (
                <div key={index} className={`activity-item ${event.type}`}>
                  <span className="activity-time">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="activity-type">{event.type}</span>
                  {event.data && (
                    <span className="activity-details">
                      Hemisphere {event.data.hemisphere_id} - 
                      {event.data.amount ? ` ${formatCurrency(event.data.amount)}` : ''}
                    </span>
                  )}
                </div>
              ))}
              {realTimeData.length === 0 && (
                <p className="no-activity">No recent activity</p>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>Payment Verification System v2.0 - Real-time Monitoring</p>
          <div className="hemisphere-legend">
            {hemispheres.slice(0, 5).map(hemisphere => (
              <div key={hemisphere.id} className="legend-item">
                <div 
                  className="legend-color" 
                  style={{ backgroundColor: `hsl(${hemisphere.id / 13 * 360}, 70%, 50%)` }}
                ></div>
                <span>{hemisphere.name}</span>
              </div>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
